import * as yup from "yup";

import {
    AUTO_START_COUNTDOWN_RANGE,
    GAME_TEXT_LIMITS,
    GAME_TIME_RANGE,
    getPrizeTitleMaxLength,
    MAX_QUIZ_OPTIONS,
    PAIR_COUNT_RANGE,
    PUZZLE_COUNT_RANGE,
    QUIZ_PASS_RANGE,
    QUIZ_TOTAL_RANGE,
} from "../constants";
import { GameLanguageCode } from "../types";

// =============== Quiz Option Schema ================
const quizOptionSchema = yup.object({
    text: yup
        .string()
        .trim()
        .required("APPWIDGET.GAME_VALIDATION_OPTION_TEXT_REQUIRED")
        .min(1, "APPWIDGET.GAME_VALIDATION_OPTION_TEXT_REQUIRED")
        .max(GAME_TEXT_LIMITS.QUIZ_OPTION, "APPWIDGET.GAME_VALIDATION_QUIZ_OPTION_MAX_LENGTH"),
    image_url: yup.string().nullable(),
});
// ============ Item Schemas ============
const puzzleItemSchema = yup.object({
    id: yup.string().required(),
    image_url: yup.string().default(""),
    title: yup
        .string()
        .default("")
        .max(GAME_TEXT_LIMITS.PUZZLE_TITLE, "APPWIDGET.GAME_VALIDATION_PUZZLE_TITLE_MAX_LENGTH"),
    description: yup
        .string()
        .default("")
        .max(GAME_TEXT_LIMITS.PUZZLE_DESCRIPTION, "APPWIDGET.GAME_VALIDATION_PUZZLE_DESCRIPTION_MAX_LENGTH"),
});

const quizItemSchema = yup.object({
    id: yup.string().required(),
    title: yup
        .string()
        .trim()
        .required("APPWIDGET.GAME_VALIDATION_TITLE_REQUIRED")
        .min(1, "APPWIDGET.GAME_VALIDATION_TITLE_REQUIRED")
        .max(GAME_TEXT_LIMITS.QUIZ_QUESTION, "APPWIDGET.GAME_VALIDATION_QUIZ_QUESTION_MAX_LENGTH"),
    image_url: yup.string().nullable().default(""),
    options: yup
        .array()
        .of(quizOptionSchema)
        .min(MAX_QUIZ_OPTIONS, "APPWIDGET.GAME_VALIDATION_OPTIONS_COUNT")
        .max(MAX_QUIZ_OPTIONS, "APPWIDGET.GAME_VALIDATION_MAX_OPTIONS")
        .default([]),
    correct_option_index: yup
        .number()
        .min(0)
        .max(MAX_QUIZ_OPTIONS - 1)
        .default(0),
});

const flipCardItemSchema = yup.object({
    id: yup.string().required(),
    image_url: yup.string().default(""),
    title: yup.string().default(""),
    description: yup.string().default(""),
});

const prizeItemSchema = yup.object({
    id: yup.string().required(),
    // 獎品名稱字數上限依語言而定（CJK 7、其餘 10）。lang 位於上一層 { lang, content }，
    // 透過 this.from[1] 取得，故用 .test() 動態判斷而非靜態 .max()。
    title: yup
        .string()
        .default("")
        .test("prize-title-max-length", "APPWIDGET.GAME_VALIDATION_PRIZE_TITLE_MAX_LENGTH", function (value) {
            if (!value) return true;
            const lang = this.from?.[1]?.value?.lang as GameLanguageCode | undefined;
            const max = lang ? getPrizeTitleMaxLength(lang) : getPrizeTitleMaxLength("en-US");
            return value.length <= max;
        }),
    type: yup.string().oneOf(["none", "qrcode", "image"]).default("none"),
    url: yup.string().default(""),
});
// ============ Language Content Schema Factory ============
const createLanguageContentSchema = <T extends yup.Maybe<yup.AnyObject>>(itemSchema: yup.ObjectSchema<T>) =>
    yup.object({
        lang: yup.string().required(),
        content: yup.array().of(itemSchema).default([]),
    });

// 未啟用的遊戲不驗證其內容。
// 否則會出現無解的死結：使用者關掉某個遊戲，但該遊戲殘留半填的項目仍會擋住儲存，
// 而該分頁在關閉狀態下只顯示「請先啟用」、內容根本點不進去，使用者永遠修不掉。
// 內容仍會照原樣送出，重新啟用時驗證即恢復。
const createGameDataSchema = <T extends yup.Maybe<yup.AnyObject>>(itemSchema: yup.ObjectSchema<T>) =>
    yup
        .array()
        .when("enabled", {
            is: true,
            then: (schema) => schema.of(createLanguageContentSchema(itemSchema)),
            otherwise: (schema) => schema.of(yup.object().default({})),
        })
        .required();

// ============ Game Settings Schemas ============
const puzzleSettingsSchema = yup.object({
    enabled: yup.boolean().default(false),
    game_time: yup.number().min(GAME_TIME_RANGE.min).max(GAME_TIME_RANGE.max).default(GAME_TIME_RANGE.default),
    puzzle_count: yup
        .number()
        .min(PUZZLE_COUNT_RANGE.min)
        .max(PUZZLE_COUNT_RANGE.max)
        .default(PUZZLE_COUNT_RANGE.default),
    bgm: yup.string().default(""),
    bg: yup.string().default(""),
    bgmFile: yup.mixed().nullable().default(null),
    bgFile: yup.mixed().nullable().default(null),
    data: createGameDataSchema(puzzleItemSchema),
});

const quizSettingsSchema = yup.object({
    enabled: yup.boolean().default(false),
    total_per_round: yup.number().min(QUIZ_TOTAL_RANGE.min).max(QUIZ_TOTAL_RANGE.max).default(QUIZ_TOTAL_RANGE.default),
    pass_count: yup
        .number()
        .min(QUIZ_PASS_RANGE.min)
        .max(QUIZ_PASS_RANGE.max)
        .default(QUIZ_PASS_RANGE.default)
        .when("total_per_round", {
            is: (total: number) => total !== undefined,
            then: (schema) =>
                schema.test(
                    "pass-count-not-exceed-total",
                    "APPWIDGET.GAME_VALIDATION_PASS_COUNT_EXCEED_TOTAL",
                    function (passCount) {
                        const { total_per_round } = this.parent;
                        if (passCount === undefined || total_per_round === undefined) return true;
                        return passCount <= total_per_round;
                    }
                ),
        }),
    bgm: yup.string().default(""),
    bg: yup.string().default(""),
    bgmFile: yup.mixed().nullable().default(null),
    bgFile: yup.mixed().nullable().default(null),
    data: createGameDataSchema(quizItemSchema),
});

const flipCardSettingsSchema = yup.object({
    enabled: yup.boolean().default(false),
    game_time: yup.number().min(GAME_TIME_RANGE.min).max(GAME_TIME_RANGE.max).default(GAME_TIME_RANGE.default),
    pair_count: yup.number().min(PAIR_COUNT_RANGE.min).max(PAIR_COUNT_RANGE.max).default(PAIR_COUNT_RANGE.default),
    bgm: yup.string().default(""),
    bg: yup.string().default(""),
    bgmFile: yup.mixed().nullable().default(null),
    bgFile: yup.mixed().nullable().default(null),
    data: createGameDataSchema(flipCardItemSchema),
});

const prizeCommonSchema = yup.object({
    enabled: yup.boolean().default(false),
    mode: yup.string().oneOf(["wheel", "slot"]).default("wheel"),
    auto_start_countdown: yup
        .number()
        .min(AUTO_START_COUNTDOWN_RANGE.min)
        .max(AUTO_START_COUNTDOWN_RANGE.max)
        .default(AUTO_START_COUNTDOWN_RANGE.default),
});

const prizeSettingsSchema = yup.object({
    common: prizeCommonSchema,
    // 同 createGameDataSchema 的理由：未啟用就不驗證內容。
    // 獎勵的開關位於 common.enabled，故以 sibling 路徑取值。
    data: yup
        .array()
        .when("common.enabled", {
            is: true,
            then: (schema) => schema.of(createLanguageContentSchema(prizeItemSchema)),
            otherwise: (schema) => schema.of(yup.object().default({})),
        })
        .required(),
});

// ============ Main Form Schema ============
export const gameSettingsValidationSchema = yup.object({
    enabled: yup.boolean().default(false),
    games: yup.object({
        puzzle: puzzleSettingsSchema,
        quiz: quizSettingsSchema,
        flipcard: flipCardSettingsSchema,
    }),
    prize: prizeSettingsSchema,
});

export type GameSettingsFormSchema = yup.InferType<typeof gameSettingsValidationSchema>;
